/**
 * Admin · Privacy & Retention — the trust dashboard.
 *
 * Surfaces: retention windows (CRUD) + 2FA placeholder toggle for the
 * signed-in admin + the published privacy manifesto in human-readable form.
 */
import { useEffect, useState } from "react";
import { Shield, Save, KeyRound, Trash2, Lock, Bell } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAdminWorkspace, AdminHeader, AdminEmpty } from "./_shared";
import PrivacyFooter from "../../components/PrivacyFooter";
import PushToggle from "../../components/PushToggle";
import TwoFASetup from "../../components/TwoFASetup";
import AdminDataRequests from "../../components/AdminDataRequests";

const FIELDS = [
  { key: "message_retention_days", label: "Messages & threads" },
  { key: "photo_retention_days", label: "Photos & moments" },
  { key: "attendance_log_retention_days", label: "Attendance log" },
  { key: "status_history_retention_days", label: "Status history" },
  { key: "audit_log_retention_days", label: "Audit log" },
];

export default function AdminPrivacy() {
  const { workspace, loading: wsLoading } = useAdminWorkspace();
  const [retention, setRetention] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadRetention = async () => {
    if (!workspace) return;
    try {
      const r = await api.get(
        `/admin/workspaces/${workspace.id}/retention`
      );
      setRetention(r.data);
    } catch {
      setRetention(null);
    }
  };

  useEffect(() => {
    loadRetention();
  }, [workspace?.id]);

  const save = async () => {
    if (!retention) return;
    setSaving(true);
    try {
      const body = Object.fromEntries(
        FIELDS.map((f) => [f.key, parseInt(retention[f.key] || 0, 10)])
      );
      const r = await api.patch(
        `/admin/workspaces/${workspace.id}/retention`,
        body
      );
      setRetention(r.data);
      toast("Retention settings calmly saved.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't save retention.");
    } finally {
      setSaving(false);
    }
  };

  if (wsLoading) return null;
  if (!workspace)
    return (
      <AdminEmpty testid="admin-privacy-no-ws">No workspace yet.</AdminEmpty>
    );

  return (
    <div data-testid="admin-privacy-screen">
      <AdminHeader
        eyebrow="Privacy & retention"
        title="Hold our promises, calmly"
      />
      <div className="mt-2 text-sm text-[#525252] max-w-2xl" data-manifesto-negation>
        Privacy-first by design. No tracking, no surveillance — just human-updated
        status. Configure how long data lives, and turn on two-factor sign-in.
      </div>

      {/* Trust language card */}
      <section
        data-testid="admin-privacy-manifesto"
        className="mt-8 rounded-3xl bg-white border hairline p-6 flex items-start gap-4"
      >
        <span
          aria-hidden
          className="h-11 w-11 rounded-2xl bg-[#F2EFEA] flex items-center justify-center shrink-0"
        >
          <Shield size={16} strokeWidth={1.6} className="text-[#1A1B2E]" />
        </span>
        <div>
          <span className="eyebrow">Our trust language</span>
          <p className="mt-2 font-serif text-lg text-[#171717] leading-snug" data-manifesto-negation>
            Privacy-first. No tracking. Human-updated child status only.
          </p>
          <p className="mt-2 text-xs text-[#525252]">
            This sentence appears on the sign-in screen for every family.
          </p>
        </div>
      </section>

      {/* Retention */}
      <section
        data-testid="admin-privacy-retention"
        className="mt-8 rounded-3xl bg-white border hairline p-6"
      >
        <div className="flex items-baseline gap-3">
          <Trash2 size={14} strokeWidth={1.6} className="text-[#8A9984]" />
          <span className="eyebrow">Data retention</span>
        </div>
        <p className="mt-2 text-xs text-[#525252]">
          Set to <strong>0</strong> to keep forever. Otherwise data older than the
          window is gently purged once daily.
        </p>
        {retention === null ? (
          <p className="mt-5 font-serif italic text-[#6A6863]">A quiet moment…</p>
        ) : (
          <div className="mt-5 space-y-4">
            {FIELDS.map((f) => (
              <div
                key={f.key}
                className="flex items-center justify-between gap-4"
              >
                <label
                  htmlFor={`retention-${f.key}`}
                  className="text-sm text-[#2C2B29] flex-1"
                >
                  {f.label}
                </label>
                <div className="inline-flex items-center gap-2 bg-[#F2EFEA] rounded-full px-4 py-2">
                  <input
                    id={`retention-${f.key}`}
                    type="number"
                    min={0}
                    max={3650}
                    value={retention[f.key] ?? 0}
                    onChange={(e) =>
                      setRetention({
                        ...retention,
                        [f.key]: e.target.value,
                      })
                    }
                    data-testid={`admin-privacy-retention-${f.key}`}
                    className="w-16 bg-transparent text-sm font-medium outline-none text-right"
                  />
                  <span className="text-[10px] tracking-[0.18em] uppercase text-[#525252]">
                    days
                  </span>
                </div>
              </div>
            ))}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="admin-privacy-retention-save"
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#132619] text-white text-xs tracking-[0.16em] uppercase font-medium hover:bg-[#0b2117] transition-colors disabled:opacity-50"
              >
                <Save size={13} strokeWidth={1.7} />
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Push toggle (this device) */}
      <section
        data-testid="admin-privacy-push"
        className="mt-6 rounded-3xl bg-white border hairline p-6"
      >
        <div className="flex items-baseline gap-3">
          <Bell size={14} strokeWidth={1.6} className="text-[#1F3A2A]" />
          <span className="eyebrow">Push on this device</span>
        </div>
        <p className="mt-2 text-xs text-[#525252]">
          Receive a gentle nudge on this device&rsquo;s home screen — nothing
          alarming, just the same calm whispers we send in the app.
        </p>
        <div className="mt-4">
          <PushToggle testid="admin-push-toggle" />
        </div>
      </section>

      {/* 2FA — real TOTP enrollment flow */}
      <section
        data-testid="admin-privacy-2fa"
        className="mt-6 rounded-3xl bg-white border hairline p-6"
      >
        <div className="flex items-baseline gap-3">
          <KeyRound size={14} strokeWidth={1.6} className="text-[#1F3A2A]" />
          <span className="eyebrow">Two-factor sign-in</span>
        </div>
        <p className="mt-2 text-xs text-[#525252]">
          A six-digit code at sign-in. Use any TOTP authenticator
          (1Password, Authy, Google Authenticator).
        </p>
        <div className="mt-4">
          <TwoFASetup testid="admin-twofa" />
        </div>
      </section>

      {/* GDPR — admin roster of pending requests */}
      <div className="mt-6">
        <AdminDataRequests testid="admin-data-requests" />
      </div>

      <PrivacyFooter testId="admin-privacy-manifesto-footer" />
    </div>
  );
}
